import {
    AccordionBody,
    Button,
    Draggable,
    ValidatedDropDownList,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from 'i18next';
import { TariffPeriod, TariffsInfo } from 'models/api/tariff';
import { useTariffInfoState } from 'providers/TariffInfoProvider';
import { useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { Accordion, AccordionHeader, Col, Row } from 'reactstrap';
import { TariffAccesses } from './TariffsModal';
import TariffsModalPeriodsServices from './TariffsModalPeriodsServices';
import TariffsModalProcedureParams from './TariffsModalProcedureParams';

const TariffsModalPeriods = ({ fullAccess }: TariffAccesses) => {
    const { setFocus } = useFormContext<TariffsInfo>();
    const { fields, swap, append, remove } = useFieldArray<TariffsInfo>({ name: 'periods' });
    const [open, setOpen] = useState('');
    const { proceduresDef, proceduresDefLoading, parentService, parentServiceLoading } =
        useTariffInfoState();

    const accordionProps = {
        open: open,
        toggle: (id: string) => setOpen((prev) => (prev === id ? '' : id)),
    };

    const reorderPeriods = (source: number, target: number) => {
        swap(source, target);
    };

    const emptyPeriod: TariffPeriod = useMemo(
        () => ({
            id: 0,
            term: 0,
            type: '',
            proceduresDefId: typeof proceduresDef[0]?.id === 'number' ? proceduresDef[0]?.id : 0,
            parentServices: [],
            proceduresParams: [],
        }),
        [proceduresDef]
    );

    const addPeriod = () => {
        append(emptyPeriod, { shouldFocus: true });
        setTimeout(() => setFocus(`periods.${fields.length}.term`), 1);
    };

    const deletePeriod = (idx: number) => {
        remove(idx);
    };

    return (
        <>
            <Row className="periods mb-3">
                <Accordion {...accordionProps}>
                    {fields.map(({ id }, idx) => (
                        <Draggable<number>
                            key={id}
                            typeKey="PERIODS"
                            sourceKey={idx}
                            onItemReorder={reorderPeriods}
                        >
                            <AccordionHeader
                                tag="div"
                                targetId={id}
                                className="periods__accordion-header"
                            >
                                <div>
                                    {`${t('BackOfficePeriod')} ${idx + 1}`}
                                    {fullAccess && (
                                        <FontAwesomeIcon
                                            icon={faTrashCan}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                deletePeriod(idx);
                                            }}
                                            className="text-danger"
                                        />
                                    )}
                                </div>
                            </AccordionHeader>

                            <AccordionBody
                                accordionId={id}
                                toggle={(val) => val && setOpen(val.toString())}
                            >
                                <Row>
                                    <Col>
                                        <ValidatedInput<TariffsInfo>
                                            name={`periods.${idx}.term`}
                                            label={t('BackOfficeTerm')}
                                            disabled={!fullAccess}
                                        />
                                    </Col>
                                    <Col>
                                        <ValidatedDropDownList<TariffsInfo>
                                            name={`periods.${idx}.proceduresDefId`}
                                            label={t('BackOfficeProceduresSet')}
                                            items={proceduresDef}
                                            isLoading={proceduresDefLoading}
                                            isSearchable={false}
                                            isClearable={false}
                                            disabled={!fullAccess}
                                        />
                                    </Col>
                                </Row>
                                <TariffsModalProcedureParams
                                    periodIdx={idx}
                                    disabled={!fullAccess}
                                />
                                <hr />
                                <TariffsModalPeriodsServices
                                    periodIdx={idx}
                                    parentServiceList={parentService}
                                    parentServiceListLoading={parentServiceLoading}
                                    fullAccess={fullAccess}
                                />
                            </AccordionBody>
                        </Draggable>
                    ))}
                </Accordion>
            </Row>
            <Row>
                <div className="text-right">
                    <Button outline disabled={!fullAccess} onClick={addPeriod}>
                        <Trans i18nKey="BackOfficeAddPage" />
                    </Button>
                </div>
            </Row>
        </>
    );
};

export default TariffsModalPeriods;
